/**
 * useFirmAssets — React Query hooks dla firm-assets API.
 * Backed by server/modules/firm-assets/routes.ts
 */
import { useQuery, useMutation } from "@tanstack/react-query";
import { apiRequest, queryClient } from "@/lib/queryClient";
import type { FirmCreativeAsset } from "@shared/schema";

const KEY = (agencyClientId?: string | null) =>
  ["firm-assets", agencyClientId ?? "global"] as const;

export function useFirmAssets(agencyClientId?: string | null) {
  return useQuery<{ items: FirmCreativeAsset[] }>({
    queryKey: KEY(agencyClientId),
    queryFn: () => {
      const qs = agencyClientId ? `?agencyClientId=${agencyClientId}` : "";
      return apiRequest("GET", `/api/firm-assets${qs}`);
    },
  });
}

export interface CreateFirmAssetInput {
  agencyClientId?: string | null;
  assetType: string;
  mediaType: "image" | "video" | "audio";
  title: string;
  description?: string | null;
  fileUrl: string;
  thumbnailUrl?: string | null;
  fileSizeBytes?: number | null;
  durationSeconds?: number | null;
  width?: number | null;
  height?: number | null;
  tags?: string[] | null;
  ecosystemPhase?: string | null;
  metadata?: Record<string, unknown> | null;
}

export function useCreateFirmAsset() {
  return useMutation({
    mutationFn: (input: CreateFirmAssetInput) =>
      apiRequest("POST", "/api/firm-assets", input) as Promise<FirmCreativeAsset>,
    onSuccess: (_data, vars) => {
      queryClient.invalidateQueries({ queryKey: KEY(vars.agencyClientId ?? null) });
    },
  });
}

export function useDeleteFirmAsset(agencyClientId?: string | null) {
  return useMutation({
    mutationFn: (id: string) => apiRequest("DELETE", `/api/firm-assets/${id}`),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: KEY(agencyClientId ?? null) });
    },
  });
}

export function useToggleAssetForClient(agencyClientId?: string | null) {
  return useMutation({
    mutationFn: ({ id, clientId, enabled }: { id: string; clientId: string; enabled: boolean }) =>
      apiRequest("POST", `/api/firm-assets/${id}/toggle-client`, {
        clientId,
        enabled,
      }) as Promise<FirmCreativeAsset>,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: KEY(agencyClientId ?? null) });
      queryClient.invalidateQueries({ queryKey: ["user-media-all"] });
    },
  });
}

export interface PresignResult {
  uploadUrl: string;
  getUrl: string;
  key: string;
}

export interface ReadinessReport {
  ready: boolean;
  missing: Array<"logo" | "face" | "palette">;
  present: { logo: boolean; face: boolean; palette: boolean };
  counts: Record<string, number>;
}

export function useBrandReadiness(agencyClientId?: string | null) {
  return useQuery<ReadinessReport>({
    queryKey: ["firm-assets-readiness", agencyClientId ?? "global"],
    queryFn: () => {
      const qs = agencyClientId ? `?agencyClientId=${agencyClientId}` : "";
      return apiRequest("GET", `/api/firm-assets/readiness${qs}`);
    },
  });
}

export function usePresignUpload() {
  return useMutation({
    mutationFn: (input: { filename: string; mimeType: string }) =>
      apiRequest("POST", "/api/firm-assets/presign-upload", input) as Promise<PresignResult>,
  });
}

export interface ProxyUploadResult {
  fileUrl: string;
  key: string;
  mimeType: string;
  sizeBytes: number;
}

/**
 * Server-side proxy upload — fallback gdy CORS blokuje direct PUT do R2 z browsera
 * (np. localhost dev origin nie jest w R2 CORS allowlist). Browser POSTuje
 * multipart/form-data do backendu, backend wgrywa do R2. Wolniejsze (2 transfery
 * zamiast 1), ale eliminuje cross-origin issue.
 *
 * Używamy istniejącego helpera uploadFile() z queryClient — przepuszcza Bearer token
 * + X-Locale, zwraca sparsowany JSON, rzuca przy ne-2xx. Bez tego ten endpoint
 * dostawał "Brak tokenu autoryzacji" bo nasze sesje używają Bearer auth, nie cookies.
 */
export function useProxyUploadFile() {
  return useMutation({
    mutationFn: async (input: { file: File; folder?: string }): Promise<ProxyUploadResult> => {
      const form = new FormData();
      form.append("file", input.file);
      if (input.folder) form.append("folder", input.folder);
      const { uploadFile } = await import("@/lib/queryClient");
      return uploadFile("/api/firm-assets/upload-direct", form) as Promise<ProxyUploadResult>;
    },
  });
}

export interface ScrapedBrand {
  url: string;
  siteName: string | null;
  title: string | null;
  description: string | null;
  logoCandidates: Array<{ url: string; source: string; size?: string | null }>;
  colors: string[];
  fonts: string[];
  heroImage: string | null;
}

/** Normalizuje URL: dodaje https:// jeśli user wpisał bare domain (np. "viral.partners"). */
function normalizeUrl(raw: string): string {
  const trimmed = raw.trim();
  if (!trimmed) return trimmed;
  if (/^https?:\/\//i.test(trimmed)) return trimmed;
  return `https://${trimmed}`;
}

export function useScrapeWebsite() {
  return useMutation({
    mutationFn: (input: { url: string; agencyClientId?: string | null }) =>
      apiRequest("POST", "/api/firm-assets/scrape-from-url", {
        ...input,
        url: normalizeUrl(input.url),
      }) as Promise<ScrapedBrand>,
  });
}

export interface ImportFromScrapeInput {
  agencyClientId?: string | null;
  sourceUrl: string;
  logos?: Array<{ url: string; source: string }>;
  colors?: string[];
  fonts?: string[];
  heroImage?: string | null;
  siteName?: string | null;
}

export function useImportFromScrape() {
  return useMutation({
    mutationFn: (input: ImportFromScrapeInput) =>
      apiRequest("POST", "/api/firm-assets/import-from-scrape", input) as Promise<{
        created: number;
        assets: FirmCreativeAsset[];
      }>,
    onSuccess: (_data, vars) => {
      queryClient.invalidateQueries({ queryKey: KEY(vars.agencyClientId ?? null) });
    },
  });
}
